/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * "Recently used" sections for the Overview page, as in AWS Management (utils/recentServices.ts)
 * and the Ops Console: kept in localStorage per signed-in user, since it is a personal convenience,
 * not account state. Never throws: with storage blocked the page still works, just unordered.
 */
export type RecentSection = { lastVisitedAt: string; visitCount: number };
type RecentMap = Record<string, RecentSection>;

const key = (userId: string) => `swift_recent_sections_${userId || "anonymous"}`;

export const getRecentSections = (userId: string): RecentMap => {
  try {
    const raw = window.localStorage.getItem(key(userId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

export const recordSectionVisit = (userId: string, sectionId: string, now = new Date()) => {
  try {
    const all = getRecentSections(userId);
    all[sectionId] = {
      lastVisitedAt: now.toISOString(),
      visitCount: (all[sectionId]?.visitCount || 0) + 1,
    };
    window.localStorage.setItem(key(userId), JSON.stringify(all));
  } catch (e) {
    // Storage unavailable: recency is a nice-to-have.
  }
};

/** "just now", "5 minutes ago", "2 days ago": the wording AWS Management and the Ops Console use. */
export const usedAgo = (iso: string, now = Date.now()): string => {
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"} ago`;
  if (s < 60) return "just now";
  if (s < 3600) return unit(Math.round(s / 60), "minute");
  if (s < 86400) return unit(Math.round(s / 3600), "hour");
  if (s < 86400 * 30) return unit(Math.round(s / 86400), "day");
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
